<script setup lang="ts">
import type { IFeedItemVideoDataClientDTO } from '~/shared/api/client/feed/feed.dto';
import { resizeImageSetLayers } from '~/shared/lib/resizeImage';

const istabletBig = computed(() =>
  useNuxtApp().$viewport.isGreaterOrEquals('tabletBig'),
);

const props = defineProps<IFeedItemVideoDataClientDTO>();

const image = computed(() => props.image_one?.file_url);
const imageWide = computed(() => props.image_two?.file_url);
const previewStyle = computed(() =>
  resizeImageSetLayers(
    istabletBig.value ? image.value : imageWide.value || image.value,
    {
      width: istabletBig.value ? 300 : 768,
      height: 184,
      mode: 'fill',
    },
  )
    .map((layer) => `background-image: ${layer}`)
    .join('; '),
);

const isInnerNewsPreviewIcon = computed(() => useRoute().name === 'news-id');

const isModalOpened = ref(false);
</script>

<template>
  <div>
    <div
      :class="$style.preview"
      :style="previewStyle"
      @click="isModalOpened = true"
    >
      <div :class="$style.preview__info">
        <div
          :class="[
            $style.preview__iconWrapper,
            { [$style.preview__iconWrapper_inner!]: isInnerNewsPreviewIcon },
          ]"
        >
          <Icon
            :class="$style.preview__icon"
            :size="20"
            name="my-icon:i-play"
          />
        </div>
        <div :class="$style.preview__title">{{ title }}</div>
      </div>
    </div>

    <ModalVideo
      v-if="subject && title"
      :is-visible="isModalOpened"
      :iframe="subject"
      :title="title"
      @close="isModalOpened = false"
    />
  </div>
</template>

<style module lang="scss">
.preview {
  position: relative;
  width: 300px;
  height: 184px;
  background: no-repeat center / cover;
  cursor: pointer;

  &::before {
    position: absolute;
    content: '';
    background: linear-gradient(0deg, #000000 0%, rgba(0, 0, 0, 0) 100%);
    opacity: 0.6;
    bottom: 0;
    left: 0;
    right: 0;
    height: 94px;
  }

  &__info {
    position: absolute;
    left: 20px;
    right: 20px;
    bottom: 20px;
    display: flex;
    flex-flow: row nowrap;
    gap: var(--space-2x);
    align-items: center;
  }

  &__title {
    @include text_body1_medium;
    color: var(--color-white);
    @include line_clamp(1);
  }

  &__icon {
    path {
      fill: var(--color-white);
    }

    &Wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 32px;
      height: 32px;
      flex-shrink: 0;
      background-color: var(--color-red-1);
      border: none;

      &_inner {
        background-color: transparent;
        border: 1px solid var(--color-white);
      }
    }
  }
}

@media (max-width: 1023px) {
  .preview {
    width: 100%;
  }
}
</style>
